import React, { useEffect, useState } from "react";
import {
  XIcon,
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
  Trash2Icon,
  ZapIcon,
} from "lucide-react";
import {
  agentInstanceService,
  agentService,
} from "../../../services/agentService";
import { toast } from "react-toastify";
import { KeyValue } from "@/components/common/ActionConfigModal";

const IntegrationsTab: React.FC<{ agent: any }> = ({ agent }) => {
  const [selectedIntegration, setSelectedIntegration] = useState<any | null>(
    null
  );
  const [selectedCapability, setSelectedCapability] = useState<any>();
  const [actions, setActions] = useState<Record<string, any>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [platforms, setPlatforms] = useState([
    {
      name: "Edular",
      logo: "https://klassapps.com/wp-content/uploads/2022/09/Edular-by-KlassApps-Logo.svg",
      status: true,
    },
    {
      name: "HubSpot",
      logo: "https://integrations.leadspark.xyz/assets/hubspot.png",
      status: false,
    },
    {
      name: "Zapier",
      logo: "https://cdn.worldvectorlogo.com/logos/zapier.svg",
      status: false,
    },
    {
      name: "Salesforce",
      logo: "https://integrations.leadspark.xyz/assets/salesforce.png",
      status: false,
    },
    {
      name: "Nopaperforms",
      logo: "https://integrations.leadspark.xyz/assets/nopaperforms-og.png",
      status: false,
    },
    {
      name: "Dynamics365",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/Microsoft_Dynamics_365_Logo_%282021%E2%80%93present%29.svg/960px-Microsoft_Dynamics_365_Logo_%282021%E2%80%93present%29.svg.png",
      status: false,
    },
  ]);

  useEffect(() => { agent.id && loadInteration() }, [agent]);

  const togglePlatformStatus = (name: string) => {
    setPlatforms((prev) =>
      prev.map((platform) =>
        platform.name === name
          ? {
              ...platform,
              status: platform.status ? false : true,
            }
          : platform
      )
    );
  };

  const toggleWebhookStatus = () => {
    if (selectedIntegration) {
      setSelectedIntegration({
        ...selectedIntegration,
        status: selectedIntegration.status === true ? false : true,
      });
    }
  };

  const openModal = (platform: any) => {
    if (platform.name === "Edular") {
      loadCapabilities()
      setSelectedIntegration({
        logo: platform.logo,
        name: actions.FAQ?.name ?? platform.name,
        status: actions.FAQ?.isActive ?? platform.status,
        webhookName:
          actions.FAQ?.name ?? `${platform.name.toLowerCase()} webhook`,
        endpoint:
          actions.FAQ?.url ??
          `https://${platform.name.toLowerCase()}.example.com/webhook`,
        method: actions.FAQ?.method ?? "POST",
        headers: actions.FAQ?.headers ?? [],
        bodyFields:
          actions.FAQ?.bodyTemplate?.map((data: any) => ({
            dataType: "fixed",
            ...data,
          })) ?? [],
      });
    } else {
      setSelectedIntegration({
        logo: platform.logo,
        name: platform.name,
        status: platform.status,
        webhookName: `${platform.name.toLowerCase()} webhook`,
        endpoint: `https://${platform.name.toLowerCase()}.example.com/webhook`,
        method: "POST",
        headers: [],
        bodyFields: [],
      });
    }

    setIsModalOpen(true);
  };

  const loadCapabilities = async () => {
    if (!agent?.agentType?.id) return;
    try {
      const agentType = await agentService.getAgentTypeById(
        agent.agentType.id
      );
      setSelectedCapability(agentType?.capabilities?.[0] || {});
    } catch (err: any) {
      toast.error(err?.message || "Failed to load capabilities");
    }
  };

  const objectToKeyValuesArray = (
    obj: Record<string, any> | undefined
  ): KeyValue[] => {
    if (!obj) return [];
    return Object.entries(obj).map(([key, value]) => ({
      key,
      value: typeof value === "string" ? value : JSON.stringify(value),
    }));
  };

  const updateField = (field: string, value: any) => {
    if (selectedIntegration) {
      setSelectedIntegration({ ...selectedIntegration, [field]: value });
    }
  };

  const addHeader = () => {
    if (selectedIntegration) {
      setSelectedIntegration({
        ...selectedIntegration,
        headers: [...selectedIntegration.headers, { key: "", value: "" }],
      });
    }
  };

  const updateHeader = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    if (selectedIntegration) {
      const headers = [...selectedIntegration.headers];
      headers[index] = { ...headers[index], [field]: value };
      setSelectedIntegration({ ...selectedIntegration, headers });
    }
  };

  const removeHeader = (index: number) => {
    if (selectedIntegration) {
      const headers = selectedIntegration.headers.filter(
        (_: any, i: number) => i !== index
      );
      setSelectedIntegration({ ...selectedIntegration, headers });
    }
  };

  const addBodyField = () => {
    if (selectedIntegration) {
      setSelectedIntegration({
        ...selectedIntegration,
        bodyFields: [
          ...selectedIntegration.bodyFields,
          {
            key: "",
            dataType: "fixed",
            value: "",
          },
        ],
      });
    }
  };

  const updateBodyField = (
    index: number,
    field: "key" | "dataType" | "value",
    value: any
  ) => {
    if (selectedIntegration) {
      const bodyFields = [...selectedIntegration.bodyFields];
      bodyFields[index] = { ...bodyFields[index], [field]: value };
      setSelectedIntegration({ ...selectedIntegration, bodyFields });
    }
  };

  const removeBodyField = (index: number) => {
    if (selectedIntegration) {
      const bodyFields = selectedIntegration.bodyFields.filter(
        (_: any, i: number) => i !== index
      );
      setSelectedIntegration({ ...selectedIntegration, bodyFields });
    }
  };

  const chatDataFields = [
    "user_id",
    "session_id",
    "message",
    "timestamp",
    "agent_id",
  ];

  const getLogoColor = (name: string): string => {
    const colors: Record<string, string> = {
      n8n: "bg-gradient-to-br from-gray-500 to-gray-600",
      HubSpot: "bg-gradient-to-br from-orange-500 to-orange-600",
      Zapier: "bg-gradient-to-br from-blue-500 to-blue-600",
      Salesforce: "bg-gradient-to-br from-red-500 to-red-600",
      "Pabbly Connect": "bg-gradient-to-br from-purple-500 to-purple-600",
    };
    return colors[name] || "bg-gray-400";
  };

  const ToggleSwitch = ({
    status,
    onToggle,
    size = "normal",
  }: {
    status: boolean;
    onToggle: () => void;
    size?: "normal" | "large";
  }) => {
    const width = size === "large" ? "w-14 h-7" : "w-12 h-6";
    const translate = size === "large" ? "translate-x-7" : "translate-x-6";
    const circleSize = size === "large" ? "w-5 h-5" : "w-4 h-4";

    return (
      <div
        className={`relative inline-flex items-center ${width} cursor-pointer select-none rounded-full p-1 transition-colors ${
          status ? "bg-blue-600" : "bg-gray-200"
        }`}
        onClick={onToggle}
      >
        <div
          className={`flex transform items-center justify-center rounded-full bg-white shadow-md transition-transform duration-200 ${
            status ? translate : "translate-x-1"
          } ${circleSize}`}
        >
          {status ? (
            <CheckIcon
              className={`${
                size === "large" ? "h-3 w-3" : "h-2.5 w-2.5"
              } text-blue-600`}
            />
          ) : (
            <XIcon
              className={`${
                size === "large" ? "h-3 w-3" : "h-2.5 w-2.5"
              } text-gray-400`}
            />
          )}
        </div>
      </div>
    );
  };

  const loadInteration = async () => {
    if (!agent?.id) return;

    try {
      const result = await agentInstanceService.listAgentIntegrations(
        agent.id,
        { limit: 100, includeDeleted: true }
      );

      const actionMap: Record<string, any> = {};
      (result.items || []).forEach((action: any) => {
        if (action?.headers) {
          const headers: any = {
            ...(typeof action.headers === "string"
              ? JSON.parse(action.headers)
              : action.headers),
          };
          const formattedHeaders: KeyValue[] = objectToKeyValuesArray(headers);
          action.headers = [...formattedHeaders];
        }

        if (action?.queryParams) {
          const queryParams: any = {
            ...(typeof action.queryParams === "string"
              ? JSON.parse(action.queryParams)
              : action.queryParams),
          };
          const formattedQueryParams: KeyValue[] =
            objectToKeyValuesArray(queryParams);
          action.queryParams = [...formattedQueryParams];
        }

        if (action?.bodyTemplate) {
          const bodyTemplate: any = {
            ...(typeof action.bodyTemplate === "string"
              ? JSON.parse(action.bodyTemplate)
              : action.bodyTemplate),
          };
          const formattedBodyTemplate: KeyValue[] =
            objectToKeyValuesArray(bodyTemplate);
          action.bodyTemplate = [...formattedBodyTemplate];
        }

        actionMap[action.capabilityCode] = action;
      });
      setActions(actionMap);
    } catch (err: any) {
      console.error("Failed to load actions:", err); 
    }
  };

  const handleActionSubmit = async () => {

    if (!agent?.id || !selectedCapability) {
      toast.error("Service not in effect")
      setIsModalOpen(false)
      return;
    }

    try {
      const existingAction = actions[selectedCapability?.capabilityCode];

      const payload = {
        capabilityCode: selectedCapability.capabilityCode,
        name: selectedIntegration.webhookName,
        description: selectedIntegration.description,
        method: selectedIntegration.method,
        url: selectedIntegration.endpoint,
        headers: Object.fromEntries(
          selectedIntegration.headers
            ? selectedIntegration.headers.map((h: any) => [h.key, h.value])
            : []
        ),
        queryParams: Object.fromEntries(
          selectedIntegration.queryParams
            ? selectedIntegration.queryParams.map((p: any) => [p.key, p.value])
            : []
        ),
        bodyTemplate: Object.fromEntries(
          selectedIntegration.bodyFields
            ? selectedIntegration.bodyFields.map((p: any) => [p.key, p.value])
            : []
        ),
        isActive: selectedIntegration.status
      };

      if (existingAction) {
        // Update existing
        await agentInstanceService.updateAgentIntegration(
          agent.id,
          existingAction.id,
          payload
        );
        toast.success("Integration updated successfully");
      } else {
        // Create new
        await agentInstanceService.createAgentIntegration(agent.id, payload);
        toast.success("Integration created successfully");
      }

      // Refresh actions list
      setIsModalOpen(false)
      setSelectedCapability(null)
      loadInteration()
    } catch (err: any) {
      toast.error(err?.message || "Failed to save integration configuration");
    }
  };

  return (
    <>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-900">Integrations</h3>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {platforms.map((platform, index) => (
            <div
              key={index}
              className="group relative cursor-pointer rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl"
              onClick={() => openModal(platform)}
            >
              <div
                className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl transition-all
    ${
      platform.status
        ? "bg-emerald-50 ring-2 ring-emerald-400"
        : "bg-gray-50"
    }
    group-hover:scale-110
  `}
              >
                <img
                  src={platform.logo}
                  alt={platform.name}
                  className="h-10 w-10 object-contain"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const img = e.currentTarget;
                    img.style.display = "none";
                    img.parentElement!.innerHTML = `
        <span class="text-xs font-bold text-gray-600">
          ${platform.name.slice(0, 2).toUpperCase()}
        </span>
      `;
                  }}
                />
              </div>
              <h4 className="mb-6 text-center text-lg font-semibold text-gray-900">
                {platform.name}
              </h4>
              <div
                className="mb-2 flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <ToggleSwitch
                  status={platform.status}
                  onToggle={() => togglePlatformStatus(platform.name)}
                />
              </div>
              <p
                className={`text-center text-xs font-medium ${
                  platform.status
                    ? "text-emerald-600"
                    : "text-gray-500"
                }`}
              >
                {platform.status}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedIntegration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-gray-100 bg-white p-8">
              <div className="flex items-center justify-between">
                {/* Left side: Logo + Title */}
                <div className="flex items-center gap-4">
                  <div
                    className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl transition-all
                        ${
                          selectedIntegration.status
                            ? "bg-emerald-50 ring-2 ring-emerald-400"
                            : "bg-gray-50"
                        }
                        group-hover:scale-110
                    `}
                  >
                    <img
                      src={selectedIntegration.logo}
                      alt={selectedIntegration.name}
                      className="h-10 w-10 object-contain"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = "none";
                        img.parentElement!.innerHTML = `
                            <span class="text-xs font-bold text-gray-600">
                            ${selectedIntegration.name
                              .slice(0, 2)
                              .toUpperCase()}
                            </span>
                        `;
                      }}
                    />
                  </div>

                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {selectedIntegration.name}
                    </h2>
                    <p className="mt-1 text-lg text-gray-600">
                      Webhook Integration
                    </p>
                  </div>
                </div>

                {/* Right side: Toggle + Close */}
                <div className="flex items-center gap-4">
                  <ToggleSwitch
                    status={selectedIntegration.status}
                    onToggle={toggleWebhookStatus}
                    size="large"
                  />

                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="rounded-2xl p-3 transition-colors hover:bg-gray-100"
                  >
                    <XIcon className="h-6 w-6 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-8 overflow-y-auto p-8">
              {/* Basic Webhook Details */}
              <div className="space-y-6 border-b border-gray-100 pb-6">
                <h3 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                  <ZapIcon className="h-6 w-6 text-blue-600" />
                  Basic Webhook Details
                </h3>

                {/* Webhook Name + Method */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="lg:col-span-2">
                    <label className="mb-3 block text-sm font-semibold text-gray-900">
                      Webhook Name
                    </label>
                    <input
                      type="text"
                      value={selectedIntegration.webhookName}
                      onChange={(e) =>
                        updateField("webhookName", e.target.value)
                      }
                      className="w-full rounded-xl border border-gray-200 px-4 py-4 text-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter webhook name"
                    />
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-gray-900">
                      Method
                    </label>
                    <select
                      value={selectedIntegration.method}
                      onChange={(e) => updateField("method", e.target.value)}
                      className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-4 text-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                  </div>
                </div>

                {/* Webhook Endpoint (Full width) */}
                <div>
                  <label className="mb-3 block text-sm font-semibold text-gray-900">
                    Webhook Endpoint
                  </label>
                  <input
                    type="url"
                    value={selectedIntegration.endpoint}
                    onChange={(e) => updateField("endpoint", e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-4 text-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/webhook"
                  />
                </div>
              </div>

              {/* Headers */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                    <ZapIcon className="h-6 w-6 text-blue-600" />
                    Webhook Headers
                  </h3>
                  <button
                    onClick={addHeader}
                    className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add New Header
                  </button>
                </div>
                <div className="space-y-3">
                  {selectedIntegration.headers.map(
                    (header: any, index: number) => (
                      <div
                        key={index}
                        className="group rounded-2xl bg-gray-50 p-4"
                        style={{ minHeight: "80px" }}
                      >
                        <div className="flex h-full items-center gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Header Key"
                              value={header.key}
                              onChange={(e) =>
                                updateHeader(index, "key", e.target.value)
                              }
                              className="h-14 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <span className="flex items-center text-gray-400">
                            :
                          </span>
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Header Value"
                              value={header.value}
                              onChange={(e) =>
                                updateHeader(index, "value", e.target.value)
                              }
                              className="h-14 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            onClick={() => removeHeader(index)}
                            className="flex h-full items-center justify-center rounded-xl p-3 opacity-100 transition-colors hover:bg-red-100 hover:text-red-600"
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                    <ZapIcon className="h-6 w-6 text-blue-600" />
                    Webhook Body
                  </h3>
                  <button
                    onClick={addBodyField}
                    className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add New Body Field
                  </button>
                </div>
                <div className="space-y-3">
                  {selectedIntegration.bodyFields.map(
                    (field: any, index: number) => (
                      <div
                        key={index}
                        className="group rounded-2xl bg-gray-50 p-4"
                        style={{ minHeight: "80px" }}
                      >
                        <div className="flex h-full items-center gap-4">
                          {/* Key field - fixed height */}
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Body Key"
                              value={field.key}
                              onChange={(e) =>
                                updateBodyField(index, "key", e.target.value)
                              }
                              className="h-14 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          {/* Radio buttons - fixed height */}
                          {/* <div className="flex h-14 min-w-[180px] rounded-xl bg-gray-100 p-1">
                            <label className="relative flex-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`data-type-${index}`}
                                checked={field.dataType === "fixed"}
                                onChange={() =>
                                  updateBodyField(index, "dataType", "fixed")
                                }
                                className="sr-only"
                              />
                              <div
                                className={`flex h-full items-center justify-center rounded-lg text-sm font-medium transition-all
                                                                ${
                                                                  field.dataType ===
                                                                  "fixed"
                                                                    ? "bg-white text-blue-600 shadow"
                                                                    : "text-gray-500 hover:text-gray-700"
                                                                }
                                                            `}
                              >
                                Fixed
                              </div>
                            </label>

                            <label className="relative flex-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`data-type-${index}`}
                                checked={field.dataType === "chat"}
                                onChange={() =>
                                  updateBodyField(index, "dataType", "chat")
                                }
                                className="sr-only"
                              />
                              <div
                                className={`flex h-full items-center justify-center rounded-lg text-sm font-medium transition-all
                                                                ${
                                                                  field.dataType ===
                                                                  "chat"
                                                                    ? "bg-white text-blue-600 shadow"
                                                                    : "text-gray-500 hover:text-gray-700"
                                                                }
                                                            `}
                              >
                                Chat
                              </div>
                            </label>
                          </div> */}

                          {/* Value field - fixed height */}
                          <div className="relative flex-1">
                            {field.dataType === "fixed" ? (
                              <input
                                type="text"
                                placeholder="Fixed value"
                                value={field.value}
                                onChange={(e) =>
                                  updateBodyField(
                                    index,
                                    "value",
                                    e.target.value
                                  )
                                }
                                className="h-14 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-10 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <select
                                value={field.value}
                                onChange={(e) =>
                                  updateBodyField(
                                    index,
                                    "value",
                                    e.target.value
                                  )
                                }
                                className="h-14 w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-3 pr-10 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select chat data</option>
                                {chatDataFields.map((chatField, i) => (
                                  <option key={i} value={chatField}>
                                    {chatField}
                                  </option>
                                ))}
                              </select>
                            )}
                            {field.dataType !== "fixed" && (
                              <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            )}
                          </div>

                          {/* Remove button - fixed height */}
                          <div className="flex h-14 w-12 items-center justify-center">
                            <button
                              onClick={() => removeBodyField(index)}
                              className="rounded-xl p-3 opacity-100 transition-colors hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 z-10 border-t border-gray-100 bg-gray-50 p-8">
              <div className="flex justify-end gap-4">
                <button className="flex items-center gap-3 rounded-2xl bg-emerald-600 px-8 py-4 font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-xl">
                  <ZapIcon className="h-5 w-5" />
                  Test Integration
                </button>
                <button
                  className="rounded-2xl bg-blue-600 px-8 py-4 font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-xl"
                  onClick={handleActionSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegrationsTab;
